import { validImageTypes } from '~/util/uploadToS3Bucket/utils/getUploadDataInput';

export type LoadedImageData = {
  src: string;
  intrinsicSize: { width: number; height: number };
};

/**
 * Takes the first file from a FileList, validates it's a supported image format,
 * and returns a Promise that resolves with the image's data URL src and natural dimensions.
 * Rejects if no files are provided, unsupported format, or image fails to load.
 */
const getImageSrcFromFileList = (
  fileList: FileList,
): Promise<LoadedImageData> =>
  new Promise((resolve, reject) => {
    if (fileList.length === 0) {
      reject('No files found in the FileList.');
      return;
    }

    const file = fileList[0];

    // Validate file type against supported image types
    if (!validImageTypes.includes(file.type as any)) {
      reject(
        `Unsupported image format: ${file.type}. Supported formats are: ${validImageTypes.join(', ')}.`,
      );
      return;
    }

    const reader = new FileReader();

    // Once the file is read successfully, resolve the promise with the result
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target && typeof e.target.result === 'string') {
        const src = e.target.result;

        // Create an image element to get the intrinsic dimensions
        const img = new Image();
        img.src = src;

        img.onload = () => {
          const intrinsicSize = {
            width: img.naturalWidth,
            height: img.naturalHeight,
          };

          resolve({ src, intrinsicSize });
        };

        img.onerror = () => {
          reject('Failed to load image to determine intrinsic size.');
        };
      } else {
        reject('FileReader failed to provide a valid result.');
      }
    };

    // If an error occurs while reading, reject the promise
    reader.onerror = error => {
      reject(error);
    };

    // Read the file as a data URL
    reader.readAsDataURL(file);
  });

export default getImageSrcFromFileList;
